import React from 'react';
import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import { observer } from 'mobx-react';

import { defaulComparator, stringSearchService } from '@services/StringSearchService';
import apiService from '@services/apiService';

import { ModalButtonType, type IModalDialogContent, type ModalDialogOptions, type ModalWindow } from '@components/Modal';
import type { ChatResponseItem } from '@root/models';
import { Loader } from '@components/Loader';
import { ImgFlash } from '@components/Images/ImgFlash';

type SettingsDialogProps = {
  onClose: () => void;
  chats: ChatResponseItem[];
  onSelectedChat: (chatId: string) => void;
};

@observer
export default class SettingsDialog extends React.Component<SettingsDialogProps> implements IModalDialogContent<void> {
  @observable
  private _searchTerm: string = "";

  @observable
  private _chats: ChatResponseItem[] = [];

  @observable
  private _useVectorSearch = false;

  @observable
  private _qdrantConfigured = false;

  @observable
  private _vectorResults: { chatId: string; score: number; content: string; chatName?: string; modifiedAt?: number }[] = [];

  @observable
  private _vectorSearchLoading = false;

  @observable
  private _vectorSearchError: string | null = null;

  @observable
  private _textSearchResults: { id: string; name: string; keywords: string[]; modifiedAt: number }[] | null = null;

  @observable
  private _textSearchLoading = false;

  private _vectorSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private _textSearchTimer: ReturnType<typeof setTimeout> | null = null;

  private _window: ModalWindow<void> | null = null;

  constructor(props: SettingsDialogProps) {
    super(props);
    this._chats = props.chats;
    makeObservable(this);
    void this._loadConfig();
  }

  componentDidUpdate(prevProps: SettingsDialogProps) {
    if (prevProps.chats !== this.props.chats) {
      this._chats = this.props.chats;
    }
  }

  private async _loadConfig() {
    const data = await apiService.get<{ qdrantConfigured: boolean }>('/api/search/config');
    runInAction(() => {
      this._qdrantConfigured = data.qdrantConfigured;
      if (data.qdrantConfigured) {
        this._useVectorSearch = true;
      }
    });
  }

  public getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
    this._window = window;
    return {
      title: 'Search Chats',
      closeByEscape: true
    };
  }

  @action
  private _onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this._searchTerm = event.target.value;
    if (this._useVectorSearch) {
      this._scheduleVectorSearch();
    } else {
      this._scheduleTextSearch();
    }
  };

  @action
  private clearSearch = () => {
    this._searchTerm = '';
    this._vectorResults = [];
    this._vectorSearchError = null;
    this._textSearchResults = null;
  };

  @action
  private _toggleVectorSearch = () => {
    this._useVectorSearch = !this._useVectorSearch;
    this._vectorResults = [];
    this._vectorSearchError = null;
    this._textSearchResults = null;
    if (this._useVectorSearch && this._searchTerm.trim()) {
      this._scheduleVectorSearch();
    } else if (!this._useVectorSearch && this._searchTerm.trim()) {
      this._scheduleTextSearch();
    }
  };

  private _scheduleVectorSearch() {
    if (this._vectorSearchTimer) clearTimeout(this._vectorSearchTimer);
    this._vectorSearchTimer = setTimeout(() => void this._doVectorSearch(), 500);
  }

  private _scheduleTextSearch() {
    if (this._textSearchTimer) clearTimeout(this._textSearchTimer);
    this._textSearchTimer = setTimeout(() => void this._doTextSearch(), 300);
  }

  private async _doTextSearch() {
    const query = this._searchTerm.trim();
    if (!query) {
      runInAction(() => { this._textSearchResults = null; });
      return;
    }
    runInAction(() => { this._textSearchLoading = true; });
    try {
      const data = await apiService.get<{ chats: { id: string; name: string; keywords: string[]; modifiedAt: number }[] }>(
        `/api/search/chats?q=${encodeURIComponent(query)}`
      );
      runInAction(() => { this._textSearchResults = data.chats; });
    } catch {
      runInAction(() => { this._textSearchResults = null; });
    } finally {
      runInAction(() => { this._textSearchLoading = false; });
    }
  }

  private async _doVectorSearch() {
    const query = this._searchTerm.trim();
    if (!query) {
      runInAction(() => { this._vectorResults = []; this._vectorSearchError = null; });
      return;
    }
    action(() => { this._vectorSearchLoading = true; this._vectorSearchError = null; })();
    try {
      const data = await apiService.post<{ results: { chatId: string; score: number; content: string }[] }>(
        '/api/search/vector',
        { query, limit: 20 }
      );
      runInAction(() => { this._vectorResults = data.results; });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      runInAction(() => { this._vectorSearchError = msg; this._vectorResults = []; });
    } finally {
      runInAction(() => { this._vectorSearchLoading = false; });
    }
  }

  @computed
  get searchTerms(){
    const searchStr = this._searchTerm;
    return searchStr.split(/[ ,]+/).filter(x => !!x);
  }
  
  @computed
  private get filteredChats(): ChatResponseItem[] {
    // Vector search mode: return results directly from server (includes chats outside the loaded 200)
    if (this._useVectorSearch) {
      if (this._vectorResults.length === 0) return [];
      // Build local lookup for already-loaded chats (to get full metadata)
      const chatMap = new Map(this._chats.map(c => [c.id, c]));
      return this._vectorResults.map(r => {
        const loaded = chatMap.get(r.chatId);
        if (loaded) return loaded;
        // Chat not in local cache — synthesise a minimal ChatResponseItem from vector result
        return {
          id: r.chatId,
          name: r.chatName ?? r.chatId,
          model: '',
          modifiedAt: r.modifiedAt ?? 0,
          createdAt: 0,
          keywords: [],
          isNameGenerated: false,
        } as ChatResponseItem;
      });
    }

    // Text search mode: results come from server (all chats)
    if (this._textSearchResults !== null) {
      const chatMap = new Map(this._chats.map(c => [c.id, c]));
      return this._textSearchResults.map(r => {
        const loaded = chatMap.get(r.id);
        if (loaded) return loaded;
        return {
          id: r.id,
          name: r.name,
          model: '',
          modifiedAt: r.modifiedAt,
          createdAt: 0,
          keywords: r.keywords ?? [],
          isNameGenerated: false,
        } as ChatResponseItem;
      });
    }

    // Default: show all loaded chats (no search term entered yet)
    return this._chats;
  }

  @action
  private _onChatSelect = (chatId: string) => {
    this.props.onSelectedChat(chatId);
    this._window?.close(ModalButtonType.Close); // Close the modal after selection
  };

  private _renderText = (text: string | undefined | null) => {
    const { searchTerms } = this;

    const parts = stringSearchService.markMatches(text, searchTerms, defaulComparator);
    return (<>
      {parts.map((p, index) => p.isMatch ? <span className="text-match" key={'t' + index}>{p.text}</span> : p.text)}
    </>);
  };

  render() {
    return (
      <>
        <style>{`
          .search-chat-search-container {
            display: flex;
            flex-direction: column;
            gap: 16px;
            max-width: 600px;
            height: 500px;
            max-height: 60vh;
          }

          .search-input-container {
            display: flex;
            align-items: center;
            gap: 8px;
            position: relative;
          }

          .search-input {
            flex: 1;
            padding: 10px 12px;
            background-color: var(--input-bg);
            color: var(--text-primary);
            border: 1px solid var(--border-color);
            border-radius: 6px;
            font-size: 14px;
            outline: none;
            transition: border-color 0.2s ease;
          }

          .search-input:focus {
            border-color: var(--accent-color);
          }

          .search-clear-btn {
            position: absolute;
            right: 36px;
            background: none;
            border: none;
            color: var(--text-muted);
            cursor: pointer;
            font-size: 16px;
            padding: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: color 0.2s ease;
          }

          .search-clear-btn:hover {
            color: var(--text-primary);
          }

          .vector-search-btn {
            position: absolute;
            right: 8px;
            background: none;
            border: none;
            cursor: pointer;
            font-size: 14px;
            padding: 4px 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 4px;
            transition: background-color 0.2s ease, color 0.2s ease;
            color: var(--text-muted);
            title: "Vector search";
          }

          .vector-search-btn.active {
            background-color: var(--accent-color);
            color: #fff;
          }

          .vector-search-btn:hover:not(.active) {
            background-color: var(--hover-bg);
            color: var(--text-primary);
          }

          .vector-search-error {
            font-size: 12px;
            color: var(--error-color, #e53e3e);
            padding: 0 4px;
          }

          .results-count {
            font-size: 12px;
            color: var(--text-muted);
            padding: 0 4px;
          }

          .search-chat-list {
            flex: 1;
            overflow-y: auto;
            border: 1px solid var(--border-color);
            border-radius: 6px;
            background-color: var(--bg-primary);
          }

          .empty-state {
            padding: 20px;
            text-align: center;
            color: var(--text-muted);
            font-size: 14px;
          }

          .search-chat-item {
            padding: 12px 16px;
            border-bottom: 1px solid var(--border-color);
            cursor: pointer;
            transition: background-color 0.2s ease;
            background-color: transparent;
          }

          .search-chat-item:hover {
            background-color: var(--hover-bg);
          }

          .search-chat-item:last-child {
            border-bottom: none;
          }

          .search-chat-name {
            font-size: 14px;
            font-weight: 500;
            color: var(--text-primary);
            margin-bottom: 4px;
          }

          .search-chat-keywords {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
            margin-top: 6px;
          }

          .keyword-tag {
            font-size: 11px;
            background-color: var(--bg-secondary);
            color: var(--text-secondary);
            padding: 2px 6px;
            border-radius: 3px;
            border: 1px solid var(--border-color);
          }

          .search-chat-date {
            font-size: 11px;
            color: var(--text-muted);
            margin-top: 4px;
          }

          @media (max-width: 768px) {
            .search-chat-search-container {  
              max-height: 75vh;
            }
            .modal-content{  
              max-height: 95vh;
            }
          }
        `}</style>

        <div className="search-chat-search-container">
          {/* Search Input */}
          <div className="search-input-container">
            <input
              type="text"
              placeholder={this._useVectorSearch ? "Semantic search (describe what you're looking for)..." : "Search chats by name or keywords..."}
              value={this._searchTerm}
              onChange={this._onSearchChange}
              className="search-input"
              autoFocus
            />
            {this._searchTerm && (
              <button
                onClick={this.clearSearch}
                className="search-clear-btn"
                style={this._qdrantConfigured ? undefined : { right: 8 }}
                title="Clear search"
              >
                ×
              </button>
            )}
            {this._qdrantConfigured && (
              <button
                onClick={this._toggleVectorSearch}
                className={`vector-search-btn${this._useVectorSearch ? ' active' : ''}`}
                title={this._useVectorSearch ? 'Vector search enabled (click to disable)' : 'Enable vector / semantic search'}
              >
                <ImgFlash size={18} />
              </button>
            )}
          </div>

          {/* Results Count / Status */}
          {(this._vectorSearchLoading || this._textSearchLoading) ? (
            <div className="results-count"><Loader size="xsm" /> Searching…</div>
          ) : this._vectorSearchError ? (
            <div className="vector-search-error">{this._vectorSearchError}</div>
          ) : (
            <div className="results-count">
              {this._useVectorSearch
                ? (this._searchTerm.trim()
                    ? `${this.filteredChats.length} result${this.filteredChats.length !== 1 ? 's' : ''} (semantic)`
                    : `${this._chats.length} chats`)
                : (this._textSearchResults !== null
                    ? `${this.filteredChats.length} of all chats`
                    : `${this._chats.length} chats`)
              }
            </div>
          )}

          {/* Chat List */}
          <div className="search-chat-list">
            {this.filteredChats.length === 0 ? (
              <div className="empty-state">
                {this._searchTerm
                  ? (this._vectorSearchLoading || this._textSearchLoading)
                    ? 'Searching…'
                    : 'No chats found matching your search'
                  : 'No chats available'}
              </div>
            ) : (
              this.filteredChats.map(chat => (
                <div
                  key={chat.id}
                  onClick={() => this._onChatSelect(chat.id)}
                  className="search-chat-item"
                >
                  <div className="search-chat-name">
                    {this._useVectorSearch ? chat.name || 'Untitled Chat' : this._renderText(chat.name) || 'Untitled Chat'}
                  </div>
                  
                  {this._useVectorSearch && (() => {
                    const vr = this._vectorResults.find(r => r.chatId === chat.id);
                    return vr ? (
                      <div className="search-chat-date" style={{ marginTop: 4 }}>
                        Similarity: {(vr.score * 100).toFixed(1)}%
                        {vr.content && <span style={{ marginLeft: 8, fontStyle: 'italic' }}>"{vr.content.slice(0, 80)}{vr.content.length > 80 ? '…' : ''}"</span>}
                      </div>
                    ) : null;
                  })()}

                  {!this._useVectorSearch && chat.keywords && chat.keywords.length > 0 && (
                    <div className="search-chat-keywords">
                      {chat.keywords.filter(x => !!x).map((keyword, index) => (
                        <span key={index} className="keyword-tag">
                          {this._renderText(keyword)}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {chat.modifiedAt && (
                    <div className="search-chat-date">
                      Last modified: {new Date(chat.modifiedAt).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </>
    );
  }
}